export const decimalSep = ".";
export const thousandSep = ",";

function normalizeMoneySeparators(raw: string): string | null {
	const value = raw.replace(/\s+/g, "");
	if (value === "") return "";
	if (!/^[0-9.,]*$/.test(value)) return null;

	if (value.includes(",") && value.includes(".")) {
		const lastComma = value.lastIndexOf(",");
		const lastDot = value.lastIndexOf(".");
		const detectedDecimalSep = lastDot > lastComma ? "." : ",";
		const parts = value.split(detectedDecimalSep);
		const left = parts.shift() || "";
		const right = parts.join("");
		const integerDigits = left.replace(/[.,]/g, "");
		const fractionDigits = right.replace(/[.,]/g, "");
		const formattedInt = formatIntegerWithThousands(integerDigits || "0", thousandSep);
		return fractionDigits === "" ? `${formattedInt}${decimalSep}` : `${formattedInt}${decimalSep}${fractionDigits}`;
	}

	if (!value.includes(".")) {
		const commaCount = (value.match(/,/g) || []).length;
		if (commaCount === 1) {
			const [leftRaw, rightRaw = ""] = value.split(",");
			const left = leftRaw ?? "";
			const right = rightRaw ?? "";

			if (right.length === 0) return `${left}${decimalSep}`;
			if (right.length <= 2) return `${left}${decimalSep}${right}`;
			if (/^\d{1,3}(,\d{3})+$/.test(value)) return value;
			return `${left}${right}`;
		}

		if (commaCount > 1 && /^\d{1,3}(,\d{3})+$/.test(value)) return value;
	}

	if (!value.includes(",") && value.includes(".")) {
		const dotCount = (value.match(/\./g) || []).length;
		if (dotCount === 1) {
			const [leftRaw, rightRaw = ""] = value.split(".");
			const left = leftRaw ?? "";
			const right = rightRaw ?? "";

			if (right.length === 0) return `${left}${decimalSep}`;
			if (right.length <= 2) return `${left}${decimalSep}${right}`;
			return `${left}${right}`;
		}

		if (/^\d{1,3}(\.\d{3})+$/.test(value)) {
			return value.replace(/\./g, "");
		}
	}

	return value;
}

export function sanitizeMoneyInput(nextValue: string, previousValue: string) {
	if (nextValue === "") return "";
	const normalized = normalizeMoneySeparators(nextValue);
	return normalized === null ? previousValue : normalized;
}

function formatIntegerWithThousands(integerDigits: string, sep: string) {
	const clean = integerDigits.replace(/^0+(?=\d)/, "");
	return clean.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

export function formatMoneyInputDisplay(raw: string) {
	const value = normalizeMoneySeparators(raw);
	if (value === null || value === "") return value || "";

	const lastSep = value.lastIndexOf(decimalSep);
	const hasDecimal = lastSep !== -1;
	const endsWithSep = value.endsWith(decimalSep);

	let leftRaw = value;
	let rightRaw = "";
	if (hasDecimal) {
		leftRaw = value.slice(0, lastSep);
		rightRaw = value.slice(lastSep + 1);
	}

	const integerDigits = leftRaw.replace(/[.,]/g, "");
	const formattedInt = formatIntegerWithThousands(integerDigits || "0", thousandSep);

	if (hasDecimal && endsWithSep) return `${formattedInt}${decimalSep}`;
	if (!hasDecimal) return formattedInt;

	const fractionDigits = rightRaw.replace(/[.,]/g, "").slice(0, 8);
	if (fractionDigits === "") return formattedInt;
	return `${formattedInt}${decimalSep}${fractionDigits}`;
}

export function parseMoneyToNumber(raw?: string | number | null) {
	if (raw === null || raw === undefined) return null;
	if (typeof raw === "number") return Number.isFinite(raw) && raw >= 0 ? raw : null;

	const value = normalizeMoneySeparators(String(raw));
	if (value === null || value === "") return null;
	if (value.indexOf(decimalSep) !== value.lastIndexOf(decimalSep)) return null;

	const normalized = value.split(thousandSep).join("").replace(decimalSep, ".");
	const num = Number(normalized);
	return Number.isFinite(num) && num >= 0 ? num : null;
}

export function formatValueToDisplay(value?: string | number | null) {
	if (value === undefined || value === null || value === "") return "";
	return formatMoneyInputDisplay(String(value));
}

/**
 * Values from API/JSON may be IEEE-754 floats (e.g. 29.700000000000003).
 * Round to cents and return a plain decimal string (no thousand separators) so PUT /order keeps working.
 */
export function formatStoredMoneyForInput(value?: string | number | null) {
	if (value === undefined || value === null || value === "") return "0";
	const n = typeof value === "number" ? value : parseMoneyToNumber(String(value));
	if (n === null || !Number.isFinite(n)) return String(value);
	const rounded = Math.round(n * 100) / 100;
	return parseFloat(rounded.toFixed(2)).toString();
}
